import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ManaCost from '../components/ManaCost';
import { useManaSymbols } from '../hooks/useManaSymbols';
import { getCardBadges } from '../components/CardGrid';
import './MarketPage.css';
import ContextMenu, { ContextMenuItem } from '../components/ContextMenu';
import AddToWishListModal from '../components/AddToWishListModal';

const API = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api`;

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface MarketMover {
  cardId:       string;
  name:         string;
  setCode:      string;
  setName:      string;
  rarity:       string;
  colors:       string;
  reserved:     boolean;
  priceNow:     number;
  pricePrev:    number;
  changePct:    number;
  changeAbs:    number;
  dateNow:      string;
  datePrev:     string;
  imageSmall:   string;
  imageLarge:   string;
  borderColor?: string;
  frameEffects?: string[];
}

interface RLCard {
  cardId:       string;
  name:         string;
  setCode:      string;
  setName:      string;
  rarity:       string;
  colors:       string;
  manaCost:     string;
  typeLine:     string;
  price:        number | null;
  qtyOwned:     number;
  imageSmall:   string;
  imageLarge:   string;
  borderColor?: string;
  frameEffects?: string[];
}

interface RLSet { setCode: string; setName: string; releasedAt: string; }

const RARITY_COLOR: Record<string, string> = {
  mythic: '#e8954a', rare: '#c8a45a', uncommon: '#8ab4c8', common: '#9a9aaa',
};

const COLORS = [
  { code: 'W', label: 'White' },
  { code: 'U', label: 'Blue'  },
  { code: 'B', label: 'Black' },
  { code: 'R', label: 'Red'   },
  { code: 'G', label: 'Green' },
];

// ─── HOVER POPUP ──────────────────────────────────────────────────────────────

const POPUP_WIDTH = 320;

function HoverPopup({ imageUrl, x, y, name }: { imageUrl: string; x: number; y: number; name: string }) {
  const showLeft = x > window.innerWidth * 0.6;
  const top = Math.min(Math.max(y - 80, 10), window.innerHeight - Math.round(POPUP_WIDTH * 1.4) - 10);
  return (
    <div style={{
      position: 'fixed', top,
      left: showLeft ? x - POPUP_WIDTH - 16 : x + 20,
      width: POPUP_WIDTH, zIndex: 9999,
      pointerEvents: 'none', borderRadius: 10, overflow: 'hidden',
      boxShadow: '0 24px 60px rgba(0,0,0,0.85)',
      animation: 'popIn 0.1s ease',
    }}>
      <img src={imageUrl} alt={name} style={{ width: '100%', display: 'block' }} />
    </div>
  );
}

// ─── COLOUR PIPS ──────────────────────────────────────────────────────────────

function ColorPips({ colors, symbols }: { colors: string; symbols: Record<string, any> }) {
  if (!colors) return null;
  return (
    <span className="market-color-pips">
      {colors.split(',').filter(Boolean).map(c => {
        const sym = symbols[`{${c.trim()}}`];
        if (sym?.imageUrl) {
          return <img key={c} src={sym.imageUrl} alt={c} width={13} height={13}
                   style={{ verticalAlign: 'middle', margin: '0 1px' }} />;
        }
        // Fallback coloured dot
        const dotColors: Record<string, string> = {
          W: '#f5f0e0', U: '#4a90d9', B: '#8a6a9a', R: '#e05a4a', G: '#4a9a6a',
        };
        return (
          <span key={c} title={c} style={{
            display: 'inline-block', width: 13, height: 13,
            borderRadius: '50%', background: dotColors[c.trim()] || '#888',
            verticalAlign: 'middle', margin: '0 1px',
          }} />
        );
      })}
    </span>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function MarketPage() {
  const navigate = useNavigate();
  const symbols  = useManaSymbols();
  const [tab, setTab] = useState<'movers' | 'rl'>('movers');

  // Movers state
  const [gainers,   setGainers]   = useState<MarketMover[]>([]);
  const [losers,    setLosers]    = useState<MarketMover[]>([]);
  const [moversDatePrev, setMoversDatePrev] = useState('');
  const [moversDateNow,  setMoversDateNow]  = useState('');
  const [moversLoading,  setMoversLoading]  = useState(false);
  const [minPrice,   setMinPrice]   = useState('1.00');
  const [moverLimit, setMoverLimit] = useState(20);
  const [format,     setFormat]     = useState('');
  const [hoverImg,  setHoverImg]  = useState<string | null>(null);
  const [hoverName, setHoverName] = useState<string>('');
  const [hoverPos,  setHoverPos]  = useState<{ x: number; y: number } | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    x: number; y: number; items: ContextMenuItem[];
  } | null>(null);
  const [wishListModal, setWishListModal] = useState<{
    cardId: string; cardName: string; imageUrl?: string | null;
  } | null>(null);

  // RL state
  const [rlCards,   setRlCards]   = useState<RLCard[]>([]);
  const [rlTotal,   setRlTotal]   = useState(0);
  const [rlPage,    setRlPage]    = useState(0);
  const [rlLoading, setRlLoading] = useState(false);
  const [rlSortBy,  setRlSortBy]  = useState('price_desc');
  const [rlColors,  setRlColors]  = useState<string[]>([]);
  const [rlSetCode, setRlSetCode] = useState('');
  const [rlMinPrice,setRlMinPrice]= useState('');
  const [rlOwnedOnly, setRlOwnedOnly] = useState(false);
  const [rlSets,    setRlSets]    = useState<RLSet[]>([]);
  const rlLimit = 50;

  // ─── FETCH MOVERS ───────────────────────────────────────────────────────────

  const fetchMovers = useCallback(async () => {
    setMoversLoading(true);
    try {
      const res = await axios.get(`${API}/market/movers`, {
        params: {
          limit:     moverLimit,
          minPrice:  minPrice || '1.00',
          direction: 'both',
          format:    format || undefined,
        }
      });
      setGainers(res.data.gainers  || []);
      setLosers(res.data.losers    || []);
      setMoversDatePrev(res.data.datePrev || '');
      setMoversDateNow(res.data.dateNow   || '');
    } catch (e) {
      console.error('Failed to fetch market movers:', e);
    } finally {
      setMoversLoading(false);
    }
  }, [minPrice, moverLimit, format]);

  // ─── FETCH RL ───────────────────────────────────────────────────────────────

  const fetchRL = useCallback(async (pg = 0) => {
    setRlLoading(true);
    try {
      const res = await axios.get(`${API}/market/reserved-list`, {
        params: {
          limit:     rlLimit,
          page:      pg,
          sortBy:    rlSortBy,
          colors:    rlColors.join(',') || undefined,
          setCodes:  rlSetCode || undefined,
          minPrice:  rlMinPrice || undefined,
          ownedOnly: rlOwnedOnly || undefined,
        }
      });
      setRlCards(res.data.cards  || []);
      setRlTotal(res.data.total  || 0);
      setRlPage(pg);
    } catch (e) {
      console.error('Failed to fetch reserved list:', e);
    } finally {
      setRlLoading(false);
    }
  }, [rlSortBy, rlColors, rlSetCode, rlMinPrice, rlOwnedOnly]);

  // ─── INITIAL LOADS ──────────────────────────────────────────────────────────

  useEffect(() => { fetchMovers(); }, [fetchMovers]);
  useEffect(() => { fetchRL(0); },   [fetchRL]);
  useEffect(() => {
    axios.get(`${API}/market/reserved-list/sets`)
      .then(r => setRlSets(r.data))
      .catch(console.error);
  }, []);

  // ─── NAVIGATE TO CARD ───────────────────────────────────────────────────────

  const goToCard = (cardId: string) => {
    navigate(`/cards/${cardId}`, { state: { from: '/market' } });
  };

  const handleRowContextMenu = (cardId: string, cardName: string, imageUrl: string | null, e: React.MouseEvent) => {
    e.preventDefault();
    setHoverImg(null);
    setHoverPos(null);
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          icon: '🔍',
          label: 'View Card Detail',
          onClick: () => goToCard(cardId),
        },
        {
          icon: '📋',
          label: 'Add to Wish List',
          onClick: () => setWishListModal({ cardId, cardName, imageUrl }),
        },
      ],
    });
  };

  // ─── TOGGLE RL COLOUR ───────────────────────────────────────────────────────

  const toggleColor = (code: string) => {
    setRlColors(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  // ─── POPUP HANDLERS ─────────────────────────────────────────────────────────

  const showPopup = (card: MarketMover | RLCard, e: React.MouseEvent) => {
    setHoverImg(card.imageLarge || card.imageSmall);
    setHoverName(card.name);
    setHoverPos({ x: e.clientX, y: e.clientY });
  };
  const movePopup = (e: React.MouseEvent) => {
    if (hoverImg) setHoverPos({ x: e.clientX, y: e.clientY });
  };
  const hidePopup = () => { setHoverImg(null); setHoverPos(null); };

  // ─── RENDER ─────────────────────────────────────────────────────────────────

  return (
    <div className="market-page" onMouseLeave={hidePopup}>

      {/* Popup */}
      {hoverImg && hoverPos && !contextMenu && (
        <HoverPopup imageUrl={hoverImg} x={hoverPos.x} y={hoverPos.y} name={hoverName} />
      )}

      {/* Header */}
      <div className="market-header">
        <h1 className="market-title">Market</h1>
      </div>

      {/* Tabs */}
      <div className="market-tabs">
        <button className={`market-tab ${tab === 'movers' ? 'active' : ''}`}
                onClick={() => setTab('movers')}>
          ⚡ Market Movers
        </button>
        <button className={`market-tab ${tab === 'rl' ? 'active' : ''}`}
                onClick={() => setTab('rl')}>
          📜 Reserved List
        </button>
      </div>

      {/* ── MOVERS TAB ── */}
      {tab === 'movers' && (
        <div className="market-section">
          {/* Controls */}
          <div className="market-controls">
            <div className="market-control-group">
              <label>Min Price</label>
              <select value={minPrice} onChange={e => setMinPrice(e.target.value)}>
                <option value="0.25">$0.25+</option>
                <option value="1.00">$1.00+</option>
                <option value="5.00">$5.00+</option>
                <option value="10.00">$10.00+</option>
                <option value="25.00">$25.00+</option>
              </select>
            </div>
            <div className="market-control-group">
              <label>Show</label>
              <select value={moverLimit} onChange={e => setMoverLimit(Number(e.target.value))}>
                <option value={10}>Top 10</option>
                <option value={20}>Top 20</option>
                <option value={50}>Top 50</option>
              </select>
            </div>
            <div className="market-control-group">
              <label>Format</label>
              <select value={format} onChange={e => setFormat(e.target.value)}>
                <option value="">All Formats</option>
                <option value="standard">Standard</option>
                <option value="pioneer">Pioneer</option>
                <option value="modern">Modern</option>
                <option value="legacy">Legacy</option>
                <option value="vintage">Vintage</option>
                <option value="commander">Commander</option>
                <option value="pauper">Pauper</option>
              </select>
            </div>
            <button className="market-refresh-btn" onClick={fetchMovers}
                    disabled={moversLoading}>
              {moversLoading ? 'Loading…' : '↻ Refresh'}
            </button>
          </div>

          {moversDatePrev && moversDateNow && (
            <div className="market-date-range">
              Comparing prices: {moversDatePrev} → {moversDateNow}
            </div>
          )}

          {moversLoading ? (
            <div className="market-loading">Loading market data…</div>
          ) : (
            <div className="movers-grid">
              {/* Gainers */}
              <div className="movers-column">
                <div className="movers-column-header gainers-header">
                  ▲ Top Gainers
                </div>
                <table className="market-table">
                  <thead>
                    <tr>
                      <th style={{width:40}}></th>
                      <th>Card</th>
                      <th>Set</th>
                      <th>Prev Price</th>
                      <th>Price</th>
                      <th>Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gainers.length === 0 ? (
                      <tr><td colSpan={6} className="market-empty">No gainers found</td></tr>
                    ) : gainers.map(card => (
                      <tr key={card.cardId}
                          onClick={() => goToCard(card.cardId)}
                          onContextMenu={e => handleRowContextMenu(card.cardId, card.name, card.imageLarge || card.imageSmall || null, e)}
                          onMouseLeave={hidePopup}
                          className="market-row">
                        <td style={{width:40,padding:'4px 8px'}}
                            onMouseEnter={e => showPopup(card, e)}
                            onMouseMove={movePopup}
                            onMouseLeave={e => { e.stopPropagation(); hidePopup(); }}>
                          {card.imageSmall && <img src={card.imageSmall} alt={card.name} style={{width:36,height:50,objectFit:'cover',borderRadius:4,border:'1px solid rgba(255,255,255,0.06)'}} />}
                        </td>
                        <td onMouseEnter={e => showPopup(card, e)}
                            onMouseMove={movePopup}
                            onMouseLeave={e => { e.stopPropagation(); hidePopup(); }}>
                          <div className="market-card-name">{card.name}</div>
                          <div className="market-card-meta">
                            <ColorPips colors={card.colors} symbols={symbols} />
                            <span className="market-rarity"
                                  style={{ color: RARITY_COLOR[card.rarity] }}>
                              {card.rarity?.charAt(0).toUpperCase()}
                            </span>
                            {card.reserved && <span className="market-rl-badge">RL</span>}
                            {getCardBadges({ borderColor: card.borderColor, frameEffects: card.frameEffects }).map((b, i) => (
                              <span key={i} className={`frame-badge ${b.className}`}>{b.label}</span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span className="market-set-code">{card.setCode?.toUpperCase()}</span>
                          <div className="market-set-name">{card.setName}</div>
                        </td>
                        <td>
                          <div className="market-prev-price" title={card.datePrev ? `As of ${card.datePrev}` : undefined}>${card.pricePrev?.toFixed(2)}</div>
                        </td>
                        <td>
                          <div className="market-price">${card.priceNow?.toFixed(2)}</div>
                        </td>
                        <td>
                          <span className="market-change up">
                            ▲ {card.changePct?.toFixed(1)}%
                          </span>
                          <div className="market-change-abs up">
                            +${card.changeAbs?.toFixed(2)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Losers */}
              <div className="movers-column">
                <div className="movers-column-header losers-header">
                  ▼ Top Losers
                </div>
                <table className="market-table">
                  <thead>
                    <tr>
                      <th style={{width:40}}></th>
                      <th>Card</th>
                      <th>Set</th>
                      <th>Prev Price</th>
                      <th>Price</th>
                      <th>Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {losers.length === 0 ? (
                      <tr><td colSpan={6} className="market-empty">No losers found</td></tr>
                    ) : losers.map(card => (
                      <tr key={card.cardId}
                          onClick={() => goToCard(card.cardId)}
                          onContextMenu={e => handleRowContextMenu(card.cardId, card.name, card.imageLarge || card.imageSmall || null, e)}
                          onMouseLeave={hidePopup}
                          className="market-row">
                        <td style={{width:40,padding:'4px 8px'}}
                            onMouseEnter={e => showPopup(card, e)}
                            onMouseMove={movePopup}
                            onMouseLeave={e => { e.stopPropagation(); hidePopup(); }}>
                          {card.imageSmall && <img src={card.imageSmall} alt={card.name} style={{width:36,height:50,objectFit:'cover',borderRadius:4,border:'1px solid rgba(255,255,255,0.06)'}} />}
                        </td>
                        <td onMouseEnter={e => showPopup(card, e)}
                            onMouseMove={movePopup}
                            onMouseLeave={e => { e.stopPropagation(); hidePopup(); }}>
                          <div className="market-card-name">{card.name}</div>
                          <div className="market-card-meta">
                            <ColorPips colors={card.colors} symbols={symbols} />
                            <span className="market-rarity"
                                  style={{ color: RARITY_COLOR[card.rarity] }}>
                              {card.rarity?.charAt(0).toUpperCase()}
                            </span>
                            {card.reserved && <span className="market-rl-badge">RL</span>}
                            {getCardBadges({ borderColor: card.borderColor, frameEffects: card.frameEffects }).map((b, i) => (
                              <span key={i} className={`frame-badge ${b.className}`}>{b.label}</span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span className="market-set-code">{card.setCode?.toUpperCase()}</span>
                          <div className="market-set-name">{card.setName}</div>
                        </td>
                        <td>
                          <div className="market-prev-price" title={card.datePrev ? `As of ${card.datePrev}` : undefined}>${card.pricePrev?.toFixed(2)}</div>
                        </td>
                        <td>
                          <div className="market-price">${card.priceNow?.toFixed(2)}</div>
                        </td>
                        <td>
                          <span className="market-change down">
                            ▼ {Math.abs(card.changePct)?.toFixed(1)}%
                          </span>
                          <div className="market-change-abs down">
                            -${Math.abs(card.changeAbs)?.toFixed(2)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── RESERVED LIST TAB ── */}
      {tab === 'rl' && (
        <div className="market-section">
          {/* Controls */}
          <div className="market-controls">
            <div className="market-control-group">
              <label>Sort</label>
              <select value={rlSortBy} onChange={e => setRlSortBy(e.target.value)}>
                <option value="price_desc">Price: High → Low</option>
                <option value="price_asc">Price: Low → High</option>
                <option value="name">Name A → Z</option>
                <option value="set">Set (Newest)</option>
              </select>
            </div>
            <div className="market-control-group">
              <label>Set</label>
              <select value={rlSetCode} onChange={e => setRlSetCode(e.target.value)}>
                <option value="">All Sets</option>
                {rlSets.map(s => (
                  <option key={s.setCode} value={s.setCode}>
                    {s.setCode.toUpperCase()} — {s.setName}
                  </option>
                ))}
              </select>
            </div>
            <div className="market-control-group">
              <label>Min Price</label>
              <select value={rlMinPrice} onChange={e => setRlMinPrice(e.target.value)}>
                <option value="">Any</option>
                <option value="1">$1+</option>
                <option value="5">$5+</option>
                <option value="10">$10+</option>
                <option value="25">$25+</option>
                <option value="50">$50+</option>
                <option value="100">$100+</option>
              </select>
            </div>
            <div className="market-control-group">
              <label>Owned</label>
              <button className={`market-toggle ${rlOwnedOnly ? 'active' : ''}`}
                      onClick={() => setRlOwnedOnly(v => !v)}>
                {rlOwnedOnly ? 'Owned Only' : 'All Cards'}
              </button>
            </div>
          </div>

          {/* Colour filter */}
          <div className="market-color-filter">
            {COLORS.map(c => {
              const active = rlColors.includes(c.code);
              const sym    = symbols[`{${c.code}}`];
              return (
                <button key={c.code}
                        className={`market-color-btn ${active ? 'active' : ''}`}
                        onClick={() => toggleColor(c.code)}
                        title={c.label}>
                  {sym?.imageUrl
                    ? <img src={sym.imageUrl} alt={c.label} width={18} height={18}
                        style={{ display: 'block', filter: active ? 'none' : 'grayscale(30%) opacity(0.7)' }} />
                    : c.code}
                </button>
              );
            })}
            {rlColors.length > 0 && (
              <button className="market-color-clear" onClick={() => setRlColors([])}>
                Clear
              </button>
            )}
          </div>

          <div className="market-rl-summary">
            {rlTotal.toLocaleString()} Reserved List cards
            {rlTotal > 0 && ` — page ${rlPage + 1} of ${Math.ceil(rlTotal / rlLimit)}`}
          </div>

          {rlLoading ? (
            <div className="market-loading">Loading Reserved List…</div>
          ) : (
            <>
              <table className="market-table market-table-full">
                <thead>
                  <tr>
                    <th style={{width:40}}></th>
                    <th>Card</th>
                    <th>Type</th>
                    <th>Set</th>
                    <th>Mana</th>
                    <th>Price</th>
                    <th>Owned</th>
                  </tr>
                </thead>
                <tbody>
                  {rlCards.length === 0 ? (
                    <tr><td colSpan={7} className="market-empty">No cards found</td></tr>
                  ) : rlCards.map(card => (
                    <tr key={card.cardId}
                        onClick={() => goToCard(card.cardId)}
                        onContextMenu={e => handleRowContextMenu(card.cardId, card.name, card.imageLarge || card.imageSmall || null, e)}
                        onMouseEnter={e => showPopup(card, e)}
                        onMouseLeave={hidePopup}
                        className="market-row">
                      <td style={{width:40,padding:'4px 8px'}}>
                        {card.imageSmall && <img src={card.imageSmall} alt={card.name} style={{width:36,height:50,objectFit:'cover',borderRadius:4,border:'1px solid rgba(255,255,255,0.06)'}} />}
                      </td>
                      <td>
                        <div className="market-card-name">{card.name}</div>
                        <div className="market-card-meta">
                          <ColorPips colors={card.colors} symbols={symbols} />
                          <span className="market-rarity"
                                style={{ color: RARITY_COLOR[card.rarity] }}>
                            {card.rarity?.charAt(0).toUpperCase()}
                          </span>
                          {getCardBadges({ borderColor: card.borderColor, frameEffects: card.frameEffects }).map((b, i) => (
                            <span key={i} className={`frame-badge ${b.className}`}>{b.label}</span>
                          ))}
                        </div>
                      </td>
                      <td className="market-type-line">{card.typeLine}</td>
                      <td>
                        <span className="market-set-code">{card.setCode?.toUpperCase()}</span>
                        <div className="market-set-name">{card.setName}</div>
                      </td>
                      <td><ManaCost manaCost={card.manaCost} size={14} /></td>
                      <td>
                        {card.price != null
                          ? <span className="market-price">${card.price.toFixed(2)}</span>
                          : <span className="market-no-price">—</span>}
                      </td>
                      <td>
                        {card.qtyOwned > 0
                          ? <span className="market-owned">×{card.qtyOwned}</span>
                          : <span className="market-not-owned">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {rlTotal > rlLimit && (
                <div className="market-pagination">
                  <button disabled={rlPage === 0} onClick={() => fetchRL(rlPage - 1)}>
                    ← Prev
                  </button>
                  <span>{rlPage + 1} / {Math.ceil(rlTotal / rlLimit)}</span>
                  <button disabled={(rlPage + 1) * rlLimit >= rlTotal}
                          onClick={() => fetchRL(rlPage + 1)}>
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          onClose={() => setContextMenu(null)}
        />
      )}
      {wishListModal && (
        <AddToWishListModal
          cardId={wishListModal.cardId}
          cardName={wishListModal.cardName}
          imageUrl={wishListModal.imageUrl}
          onDone={() => setWishListModal(null)}
          onCancel={() => setWishListModal(null)}
        />
      )}
    </div>
  );
}
